/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import moment from 'moment'

import {
  EuiButton,
  EuiButtonEmpty,
  EuiCallOut,
  EuiCodeBlock,
  EuiDatePicker,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFlyout,
  EuiFlyoutBody,
  EuiFlyoutFooter,
  EuiFlyoutHeader,
  EuiForm,
  EuiFormRow,
  EuiRadioGroup,
  EuiSpacer,
  EuiText,
  EuiTitle,
  useGeneratedHtmlId,
} from '@elastic/eui'

import { useDeprovisionConsumptionOrg } from '@modules/billing-lib/hooks'
import useBillingDetails from '@modules/billing-lib/billingDetails/useBillingDetails'

import type { Moment } from 'moment'

interface Props {
  organizationId: string
  onClose: () => void
}

enum BillingModel {
  Monthly = 'monthly',
  YaaS = 'yaas',
}

const DeprovisionFlyout = ({ organizationId, onClose }: Props) => {
  const [deprovisionDate, setDeprovisionDate] = useState<Moment | null>(moment())
  const [billingModel, setBillingModel] = useState<BillingModel>(BillingModel.Monthly)
  const billingDetails = useBillingDetails()

  const { formatMessage } = useIntl()
  const deprovisionConsumptionOrgMutation = useDeprovisionConsumptionOrg()
  const flyoutTitleId = useGeneratedHtmlId({
    prefix: 'DeprovisionFlyout',
  })

  const onSubmit = () => {
    if (!deprovisionDate) {
      return
    }

    deprovisionConsumptionOrgMutation.mutate(
      {
        pathParameters: { organization_id: organizationId },
        body: {
          deprovision_date: deprovisionDate.format('YYYY-MM-DD'),
          deprovision_type: billingModel,
        },
      },
      {
        onSuccess: () => {
          billingDetails.refetch()
        },
      },
    )
  }

  const renderSuccessfulCallOut = () => {
    const isFullySuccessful = Object.values(
      deprovisionConsumptionOrgMutation.data?.steps || {},
    ).every(({ status }) => status === 'success')

    return (
      <EuiCallOut
        title={
          isFullySuccessful ? (
            <FormattedMessage
              id='deprovision-flyout.success'
              defaultMessage='Deprovisioning successful'
            />
          ) : (
            <FormattedMessage
              id='deprovision-flyout.partially-successful'
              defaultMessage='Deprovision partially successful'
            />
          )
        }
        color={isFullySuccessful ? 'success' : 'warning'}
        iconType='user'
        data-test-id={isFullySuccessful ? 'successful-callout' : 'partially-successful-callout'}
      >
        <EuiCodeBlock language='json' isCopyable={true} isVirtualized={true} overflowHeight={250}>
          {JSON.stringify(deprovisionConsumptionOrgMutation.data, null, ' ')}
        </EuiCodeBlock>
      </EuiCallOut>
    )
  }

  const renderFailedCallOut = () => (
    <EuiCallOut
      title={
        <FormattedMessage id='deprovision-flyout.error' defaultMessage='Failed to deprovision' />
      }
      color='danger'
      iconType='error'
      data-test-id='fail-callout'
    >
      <EuiCodeBlock language='json' isCopyable={true} isVirtualized={true} overflowHeight={250}>
        {JSON.stringify(deprovisionConsumptionOrgMutation.error, null, ' ')}
      </EuiCodeBlock>
    </EuiCallOut>
  )

  return (
    <EuiFlyout
      size='m'
      maxWidth={0}
      ownFocus={true}
      onClose={onClose}
      aria-labelledby={flyoutTitleId}
    >
      <EuiFlyoutHeader hasBorder={true}>
        <EuiTitle size='m'>
          <h2>
            <FormattedMessage id='deprovision-flyout.title' defaultMessage='Deprovision' />
          </h2>
        </EuiTitle>
      </EuiFlyoutHeader>

      <EuiFlyoutBody>
        <EuiText>
          <FormattedMessage
            id='deprovision-flyout.description'
            defaultMessage='Allows to migrate an organization from automated consumption to Monthly or YaaS'
          />
        </EuiText>

        <EuiSpacer />

        <EuiForm>
          <EuiFormRow
            label={<FormattedMessage id='deprovision-flyout.date' defaultMessage='Date' />}
            helpText={
              <FormattedMessage
                id='deprovision-flyout.date.help-text'
                defaultMessage='Date at which the organization needs to be deprovisioned. The consumption subscription will end on the provided date'
              />
            }
            data-test-id='deprovision-date-picker'
          >
            <EuiDatePicker
              dateFormat='YYYY-MM-DD'
              isInvalid={false}
              selected={deprovisionDate}
              maxDate={moment()}
              onChange={setDeprovisionDate}
              onClear={() => setDeprovisionDate(null)}
            />
          </EuiFormRow>

          <EuiFormRow
            label={
              <FormattedMessage
                id='deprovision-flyout.billing-model'
                defaultMessage='Billing model'
              />
            }
            helpText={
              <FormattedMessage
                id='deprovision-flyout.billing-model.help-text'
                defaultMessage='Billing model which the organization would be on, after being deprovisioned from consumption

'
              />
            }
            data-test-id='billing-model-radio'
          >
            <EuiRadioGroup
              options={[
                {
                  id: BillingModel.Monthly,
                  label: formatMessage({
                    id: 'deprovision-flyout.billing-model.monthly',
                    defaultMessage: 'Monthly',
                  }),
                },
                {
                  id: BillingModel.YaaS,
                  label: formatMessage({
                    id: 'deprovision-flyout.billing-model.yaas',
                    defaultMessage: 'YaaS',
                  }),
                },
              ]}
              idSelected={billingModel}
              onChange={(id: BillingModel) => setBillingModel(id)}
              name='Billing model'
            />
          </EuiFormRow>
        </EuiForm>

        <EuiSpacer size='m' />

        {deprovisionConsumptionOrgMutation.isSuccess && renderSuccessfulCallOut()}
        {deprovisionConsumptionOrgMutation.isError && renderFailedCallOut()}
      </EuiFlyoutBody>

      <EuiFlyoutFooter>
        <EuiFlexGroup justifyContent='spaceBetween'>
          <EuiFlexItem grow={false}>
            <EuiButtonEmpty
              iconType='cross'
              onClick={onClose}
              flush='left'
              data-test-id='close-flyout-button'
            >
              <FormattedMessage id='deprovision-flyout.cancel' defaultMessage='Cancel' />
            </EuiButtonEmpty>
          </EuiFlexItem>

          <EuiFlexItem grow={false}>
            <EuiButton
              data-test-id='deprovision-button'
              onClick={onSubmit}
              fill={true}
              isLoading={deprovisionConsumptionOrgMutation.isLoading}
              disabled={!deprovisionDate}
            >
              <FormattedMessage id='deprovision-flyout.confirm' defaultMessage='Deprovision' />
            </EuiButton>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlyoutFooter>
    </EuiFlyout>
  )
}

export default DeprovisionFlyout
